import { AmexIcon, DiscoverIcon, MastercardIcon, UnionPayIcon, VisaIcon } from "@/components/foundations/payment-icons";
import { Input as ShadcnInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/domains/shared/services/cn";
import type { InputHTMLAttributes } from "react";
import { useId, useState } from "react";

const cardTypes = [
    { pattern: /^4[0-9]{3,}$/, icon: VisaIcon },
    { pattern: /^5[1-5][0-9]{2,}$/, icon: MastercardIcon },
    { pattern: /^3[47][0-9]{2,}$/, icon: AmexIcon },
    { pattern: /^6(?:011|5[0-9]{2}|4[4-9][0-9])[0-9]{12}$/, icon: DiscoverIcon },
    { pattern: /^(62|88)[0-9]{14,17}$/, icon: UnionPayIcon },
    { pattern: /.*/, icon: MastercardIcon },
] as const;

const detectCardIcon = (number: string) => {
    const sanitized = number.replace(/\D/g, "");
    return (cardTypes.find((c) => c.pattern.test(sanitized)) ?? cardTypes[cardTypes.length - 1]).icon;
};

export const formatCardNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, "");
    const match = cleaned.match(/\d{1,4}/g);
    return match ? match.join(" ") : cleaned;
};

type PaymentInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "defaultValue" | "type" | "size"> & {
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    label?: string;
    hint?: React.ReactNode;
    isRequired?: boolean;
    inputClassName?: string;
};

export function PaymentInput({
    onChange,
    value,
    defaultValue = "",
    maxLength = 19,
    placeholder,
    label,
    hint,
    className,
    inputClassName,
    id,
    name,
    required,
    isRequired,
    autoComplete = "cc-number",
    disabled,
    ...props
}: PaymentInputProps) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const [uncontrolled, setUncontrolled] = useState(defaultValue);
    const isControlled = value !== undefined;
    const digits = (isControlled ? value : uncontrolled) ?? "";
    const Icon = detectCardIcon(digits);
    const resolvedRequired = required ?? isRequired;

    const setDigits = (next: string) => {
        const cleaned = next.replace(/\D/g, "");
        if (!isControlled) {
setUncontrolled(cleaned);
}
        onChange?.(cleaned);
    };

    return (
        <div className={cn("flex w-full flex-col gap-1.5", className)}>
            {label ? (
                <Label htmlFor={inputId}>
                    {label}
                    {resolvedRequired ? <span aria-hidden="true">*</span> : null}
                </Label>
            ) : null}
            <div className="relative">
                <span className="pointer-events-none absolute top-1/2 left-2 z-10 flex h-6 w-8.5 -translate-y-1/2 items-center justify-center" aria-hidden>
                    <Icon className="h-6 w-8.5" />
                </span>
                <ShadcnInput
                    {...props}
                    id={inputId}
                    name={name}
                    type="text"
                    inputMode="numeric"
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    required={resolvedRequired}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={cn("pl-12", inputClassName)}
                    value={formatCardNumber(digits)}
                    onChange={(e) => setDigits(e.target.value)}
                />
            </div>
            {hint ? <span className="text-sm text-muted-foreground">{hint}</span> : null}
        </div>
    );
}
